import UAParser from 'ua-parser-js';
import { get } from 'lodash';
import queryString from 'query-string';
import { ENTER, SPACE } from '../../constants/keyboardCodes';
import { CI_URL, QUIT_SEB_URL } from '../../constants/url';
import { datadogRum } from '@datadog/browser-rum';
import { dataDogAddWarning } from '../logging/datadog';

/* global chrome */
const IS_KIOSK_MODE_AVAILABLE = 'is-kiosk-mode-available';
const VERIFIED_ACCESS_EXTENSION = 'nmmekeeogclemkdhhhfiepklkgajepek';

export const isSafari = (includeMobile = false) => {
    const parser = new UAParser();
    const browserName = get(parser.getBrowser(), 'name', '');
    if (includeMobile) {
        return browserName === 'Safari' || browserName === 'Mobile Safari';
    }
    return browserName === 'Safari';
};

export const isMacOSSafari13 = () => {
    const parser = new UAParser();
    const osName = get(parser.getOS(), 'name', '');
    const browserName = get(parser.getBrowser(), 'name', '');
    const browserMajorVersion = parseInt(get(parser.getBrowser(), 'major', ''), 10);
    // "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15) \
    // AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.0 Safari/605.1.15"
    return osName === 'Mac OS' && browserName === 'Safari' && browserMajorVersion >= 13;
};

// Legacy implementation - kept for backwards compatibility
// Tests navigator.userAgent with regex
export const isIOSLegacy = () =>
    // Copied from here: http://stackoverflow.com/questions/9038625/detect-if-device-is-ios
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

export const isIOS = () => {
    // Copied from here: https://stackoverflow.com/a/9039885https://stackoverflow.com/questions/9038625/detect-if-device-is-ios
    return (
        ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
            navigator.platform,
        ) ||
        // iPad on iOS 13 detection
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
};

// i - type of release (major, minor)
const compareVersions = (version, otherVersion, i = 0) => {
    const rec = version[i];
    const act = otherVersion[i] ? otherVersion[i] : 0;

    if (rec === act) {
        return version[i + 1] ? compareVersions(version, otherVersion, i + 1) : true;
    }
    if (rec > act) {
        return true;
    }
    if (act > rec) {
        return false;
    }
    return false;
};

export const isOSVersionEqualOrGreaterThan = version => {
    const versionStr = /OS ([0-9_]*)/gi.exec(navigator.appVersion)[1];
    const versionArr = versionStr.split('_').map(Number);
    return compareVersions(versionArr, version);
};

export const isIOS13 = () =>
    // Taken from here: https://stackoverflow.com/a/58064481
    (/iPad|iPhone|iPod/.test(navigator.platform) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
    !window.MSStream;

export const isIOS15Safari = (deviceOnly = true) => {
    const { os, browser } = new UAParser().setUA(global.navigator.userAgent).getResult();
    return (
        os.name === 'Mac OS' &&
        browser.name === 'Safari' &&
        browser.version >= 15 &&
        (!deviceOnly || navigator.maxTouchPoints > 1)
    );
};

export const getURLParam = URLparam => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(URLparam);
};

export const actionOnKeyboardPress = (e, fn) => {
    if (e.code === ENTER || e.code === SPACE || e.keyCode === ENTER || e.keyCode === SPACE) {
        fn();
    }
};

export const isKioskPWA = async ({ token }) => {
    if (!(await isCompanionExtensionAvailable(true))) return { isKiosk: false, isError: false };
    return new Promise(resolve => {
        datadogRum.addAction(
            'Sending message to the mCLASS Verified Access extension for kiosk mode detection.',
        );
        const mclassHost = window.location.origin.includes('sb-')
            ? CI_URL
            : `${window.location.origin}`;
        chrome.runtime.sendMessage(
            VERIFIED_ACCESS_EXTENSION,
            {
                command: 'verifyAccess',
                mclassHost,
                authToken: token,
            },
            response => {
                datadogRum.addAction('Received Kiosk mode detection response: ', response);

                if (Array.isArray(response?.errors) && response.errors.length > 0) {
                    resolve({ isKiosk: false, isError: true });
                    return;
                }
                resolve({ isKiosk: response.isKiosk, isError: false });
            },
        );
    });
};

export const isKioskChromeApp = () => {
    const queryParameters = queryString.parse(window.location.search);
    return (
        queryParameters.e3104518cb454b7280f6ca16ae28f2ef === 'ad5b97c6-68ee-40d4-b857-1e2d31a0dd4b'
    );
};

export const storeKioskModeParameter = async ({ token }) => {
    const pwaResult = await isKioskPWA({ token });
    const isKioskModeParamsAvailable = isKioskChromeApp() || pwaResult.isKiosk;
    if (isKioskModeParamsAvailable) {
        sessionStorage.setItem(IS_KIOSK_MODE_AVAILABLE, isKioskModeParamsAvailable);
    }
    return { isError: pwaResult.isError };
};

const getKioskModeParameters = () => sessionStorage.getItem(IS_KIOSK_MODE_AVAILABLE);

export const removeKioskModeParameters = () => {
    if (sessionStorage.getItem(IS_KIOSK_MODE_AVAILABLE)) {
        sessionStorage.removeItem(IS_KIOSK_MODE_AVAILABLE);
    }
};

export const isChromeOS = () => /CrOS/.test(navigator.userAgent);

export const isCompanionExtensionAvailable = (log = false) =>
    new Promise(resolve => {
        if (!chrome?.runtime) {
            if (log) {
                dataDogAddWarning('Companion extension: chrome.runtime is not available');
                // eslint-disable-next-line no-console
                console.warn('Companion extension: chrome.runtime is not available.');
            }
            resolve(false);
            return;
        }
        chrome.runtime.sendMessage(
            VERIFIED_ACCESS_EXTENSION,
            { command: 'ping', app: 'lantern-assessment' },
            response => {
                resolve(!!response);
            },
        );
    });

export const delayKioskLaunch = () =>
    new Promise(resolve => {
        chrome.runtime.sendMessage(
            VERIFIED_ACCESS_EXTENSION,
            { command: 'delayKioskLaunch', app: 'student-assessment' },
            response => {
                resolve(response);
            },
        );
    });

export const isKioskChromeOS = () => isChromeOS() && getKioskModeParameters();

export const setLouisianaCookie = () => {
    // Make user be able to log in with the Louisiana login page
    // This is hardcode, that would be changed post MVP
    document.cookie = `loginops_id=louisiana; Domain=.amplify.com; Path=/`;
};

export const handleCloseSEB = () => {
    window.location.assign(QUIT_SEB_URL);
};

export const isAnyUrlMatchesPathname = (pathname, urlsList = []) =>
    urlsList.some(url => pathname === url);

export const isChromeBrowser = () => {
    const { userAgent } = navigator;
    return /chrome|crios|crmo/i.test(userAgent) && !/edge|edgios|edga/i.test(userAgent);
};

export const isIPadSafari = () => {
    return isIOS() && isSafari(true);
};

export const isFromARSource = () => {
    const { source } = queryString.parse(window.location.search);
    return source === 'AR';
};
